import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi.jsx";
import APIs from "../../../Constants/apis.js";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import CustomModal from "./customModal";

const normalizeBool = (val) => Boolean(val);

const getId = (item) =>
  item?.menuItem_ID ??
  item?.MenuItem_ID ??
  item?.menuItemId ??
  item?.id ??
  0;

const getName = (item) => item?.menuItem_Name ?? item?.MenuItem_Name ?? item?.name ?? "";

const getType = (item) =>
  item?.menuItem_Type ?? item?.MenuItem_Type ?? item?.type ?? "";

const getHasVariant = (item) =>
  normalizeBool(item?.menuItem_HasVariant ?? item?.MenuItem_HasVariant ?? item?.menuItemHasVariant);

const getIsVariant = (item) =>
  normalizeBool(item?.menuItem_IsVariant ?? item?.MenuItem_IsVariant ?? item?.menuItemIsVariant);

const getIsCombo = (item) => String(getType(item)).toLowerCase() === "combo";

const getIsModifierType = (item) => String(getType(item)).toLowerCase() === "modifier";

const toDisplayItem = (raw, source) => {
  const id = Number(getId(raw)) || 0;
  const name = String(getName(raw) || "");
  const type = String(getType(raw) || "");

  return {
    source, // 'modifier' | 'product'
    menuItem_ID: id,
    menuItem_Name: name,
    menuItem_Type: type,
    menuItem_HasVariant: getHasVariant(raw),
    menuItem_IsVariant: getIsVariant(raw),
    raw,
  };
};

export default function ModifierAndProductMultiSelectionModal({
  showModal,
  setShowModal,
  compID,
  selectedItems = [],
  onSubmit,
}) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // all | modifiers | products
  const [items, setItems] = useState([]);
  const [tempSelected, setTempSelected] = useState([]);

  useEffect(() => {
    if (showModal) {
      setTempSelected(Array.isArray(selectedItems) ? selectedItems : []);
    }
  }, [showModal, selectedItems]);

  const fetchItems = useCallback(async () => {
    if (!compID) return;
    try {
      setLoading(true);

      const [modsRes, itemsRes] = await Promise.all([
        axiosInstance.get("/api/Item/GetAllModifiers", { params: { CompID: compID } }),
        axiosInstance.get(APIs.GET_ALL_ITEMS_FOR_GRID, {
          params: {
            CompID: compID,
            SortBy: "MenuItem_Name",
            SortOrder: "ASC",
            Search: "",
            PageNumber: 1,
            PageSize: 2000,
            RootCat_ID: 0,
            Status: "",
          },
        }),
      ]);

      const modifiersRaw = modsRes?.data?.data || modsRes?.data || [];
      const allItemsRaw = itemsRes?.data?.data || itemsRes?.data || [];

      const modifiers = (Array.isArray(modifiersRaw) ? modifiersRaw : [])
        .map((m) => toDisplayItem(m, "modifier"))
        .filter((m) => m.menuItem_ID > 0 && m.menuItem_Name);

      const products = (Array.isArray(allItemsRaw) ? allItemsRaw : [])
        .filter((p) => {
          if (!p) return false;
          const id = Number(getId(p)) || 0;
          if (id <= 0) return false;

          // Filter requirement: no combo, no variant products
          if (getIsCombo(p)) return false;
          if (getHasVariant(p)) return false;
          if (getIsVariant(p)) return false;

          // Only products tab should contain non-modifier items
          if (getIsModifierType(p)) return false;
          return true;
        })
        .map((p) => toDisplayItem(p, "product"))
        .filter((p) => p.menuItem_ID > 0 && p.menuItem_Name);

      // Merge and de-duplicate by ID (modifier list wins)
      const byId = new Map();
      for (const m of modifiers) byId.set(m.menuItem_ID, m);
      for (const p of products) if (!byId.has(p.menuItem_ID)) byId.set(p.menuItem_ID, p);

      setItems(Array.from(byId.values()));
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [compID]);

  useEffect(() => {
    if (showModal) {
      fetchItems();
    }
  }, [showModal, fetchItems]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const base = Array.isArray(items) ? items : [];
    const tabFiltered = base.filter((it) => {
      if (activeTab === "modifiers") return it.source === "modifier";
      if (activeTab === "products") return it.source === "product";
      return true;
    });

    if (!term) return tabFiltered;
    return tabFiltered.filter((it) => it.menuItem_Name?.toLowerCase().includes(term));
  }, [items, searchTerm, activeTab]);

  const isSelected = useCallback(
    (id) => (tempSelected || []).some((x) => Number(x.menuItem_ID) === Number(id)),
    [tempSelected]
  );

  const toggleSelection = (it) => {
    setTempSelected((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.some((x) => Number(x.menuItem_ID) === Number(it.menuItem_ID));
      if (exists) return arr.filter((x) => Number(x.menuItem_ID) !== Number(it.menuItem_ID));
      return [...arr, it];
    });
  };

  const handleClose = () => {
    setShowModal(false);
    setSearchTerm("");
    setActiveTab("all");
  };

  const handleSubmit = () => {
    if (typeof onSubmit === "function") {
      onSubmit(tempSelected);
    }
    handleClose();
  };

  return (
    <CustomModal
      isOpen={showModal}
      onClose={handleClose}
      title="Select Add-ons & Products"
      subtitle="Choose existing add-ons and products (no combo, no variants)"
      modalSize="modal-lg"
      showButton={true}
      showSubmitButton={true}
      submitButtonText="Add"
      onSubmit={handleSubmit}
      themeColor={themeColor}
      isCentered={true}
    >
      <div className="p-2">
        <div className="d-flex flex-wrap gap-2 mb-3">
          {[
            { key: "all", label: "All" },
            { key: "modifiers", label: "Add-ons" },
            { key: "products", label: "Products" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className="btn btn-sm"
              onClick={() => setActiveTab(t.key)}
              style={{
                borderRadius: 999,
                border: `1px solid ${themeColor}40`,
                background: activeTab === t.key ? `${themeColor}15` : "transparent",
                color: isDarkMode ? "#e2e8f0" : "#495057",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mb-3">
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                borderRadius: 10,
                border: `1px solid ${isDarkMode ? "#4a5568" : "#D9D9D9"}`,
                background: isDarkMode ? "#2d3748" : "#F9F9F9",
                color: isDarkMode ? "#e2e8f0" : "#495057",
                paddingRight: 40,
              }}
            />
            <i
              className="fa fa-search position-absolute"
              style={{
                right: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: isDarkMode ? "#a0aec0" : "#6c757d",
              }}
            />
          </div>
        </div>

        <div
          className="border rounded"
          style={{
            maxHeight: 420,
            overflowY: "auto",
            borderColor: isDarkMode ? "#4a5568" : "#e9ecef",
            background: isDarkMode ? "#2d3748" : "#ffffff",
          }}
        >
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status" style={{ color: themeColor }}>
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center p-4" style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}>
              No items found
            </div>
          ) : (
            <div className="p-2">
              {filteredItems.map((it) => {
                const selected = isSelected(it.menuItem_ID);
                return (
                  <div
                    key={`${it.source}-${it.menuItem_ID}`}
                    className="d-flex align-items-center justify-content-between p-2 mb-2"
                    style={{
                      borderRadius: 10,
                      border: selected
                        ? `2px solid ${themeColor}`
                        : `1px solid ${isDarkMode ? "#4a5568" : "#e9ecef"}`,
                      background: selected ? `${themeColor}10` : (isDarkMode ? "#1f2937" : "#fff"),
                      cursor: "pointer",
                    }}
                    onClick={() => toggleSelection(it)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          border: selected ? `2px solid ${themeColor}` : `2px solid ${isDarkMode ? "#718096" : "#ced4da"}`,
                          background: selected ? themeColor : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {selected && <i className="fa fa-check" style={{ color: "#fff", fontSize: 10 }} />}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: isDarkMode ? "#e2e8f0" : "#212529" }}>
                          {it.menuItem_Name}
                        </div>
                        <div style={{ fontSize: 12, color: isDarkMode ? "#a0aec0" : "#6c757d" }}>
                          {it.source === "modifier" ? "Add-on" : "Product"}
                        </div>
                      </div>
                    </div>
                    <span
                      className="badge"
                      style={{
                        background: it.source === "modifier" ? `${themeColor}20` : "#6c757d20",
                        color: it.source === "modifier" ? themeColor : (isDarkMode ? "#e2e8f0" : "#495057"),
                      }}
                    >
                      {it.source === "modifier" ? "Add-on" : "Product"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <small style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}>
            Selected: {Array.isArray(tempSelected) ? tempSelected.length : 0}
          </small>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setTempSelected([])}
            style={{ color: themeColor, textDecoration: "none" }}
          >
            Clear selection
          </button>
        </div>
      </div>
    </CustomModal>
  );
}

